import type { FocusEventHandler } from 'react';
import { type TrackerCategory, trackEvent } from '../../../common/tracking/trackerService';
import type { CreateCandidate, CreateFormState } from '../../../services/create/createFormReducer';
import type {
  CreateIntermodalBookingFormModel,
  CreateIntermodalBookingGoodFormModel,
} from '../../../services/intermodal/createIntermodalReducer';
import type {
  UpdateIntermodalBookingFormModel,
  UpdateIntermodalBookingGoodFormModel,
} from '../../../services/intermodal/updateIntermodalReducer';
import type { SettingsFormState } from '../../../services/settings/settingsReducer';
import type { BookingFormState } from '../../../services/update-form/updateFormReducer';
import { useValueHasChangedCallback } from './UseValueHasChangedCallback';

interface UseTrackingTextInputProps {
  onBlur: FocusEventHandler<HTMLInputElement>;
  onFocus: FocusEventHandler<HTMLInputElement>;
}

export interface TrackerAction {
  value: string;
}

// TODO Move transformers to their respective use-cases
//      as they are NOT specific to simple-create

export const transformCreateFieldToTrackerAction = (
  field: keyof CreateCandidate | keyof CreateFormState,
): TrackerAction => {
  return { value: field };
};

export const transformUpdateFieldToTrackerAction = (
  field: keyof BookingFormState,
): TrackerAction => {
  return { value: field };
};

export const transformUpdateIntermodalFieldToTrackerAction = (
  field: keyof UpdateIntermodalBookingFormModel,
): TrackerAction => {
  return { value: field };
};

export const transformCreateIntermodalGoodsFieldToTrackerAction = (
  field: keyof CreateIntermodalBookingGoodFormModel,
): TrackerAction => {
  return { value: field };
};

export const transformCreateIntermodalFieldToTrackerAction = (
  field: keyof CreateIntermodalBookingFormModel,
): TrackerAction => {
  return { value: field };
};

export const transformUpdateIntermodalGoodsFieldToTrackerAction = (
  field: keyof UpdateIntermodalBookingGoodFormModel,
): TrackerAction => {
  return { value: field };
};

export const transformSettingsFieldToTrackerAction = (
  field: keyof SettingsFormState,
): TrackerAction => {
  return { value: field };
};

const isSensitiveAction = (action: TrackerAction): boolean => {
  return action.value === 'customer' || action.value === 'bookCustomer';
};

export const trackEditEvent = (
  action: TrackerAction,
  category: TrackerCategory,
  label?: string,
) => {
  if (label && !isSensitiveAction(action)) {
    trackEvent({
      category,
      action: 'Edited ' + action.value,
      label,
    });
  } else {
    trackEvent({
      category,
      action: 'Edited ' + action.value,
    });
  }
};

export const useTrackingTextInput = (
  action: TrackerAction,
  category: TrackerCategory,
): UseTrackingTextInputProps => {
  const { onBlur, onFocus } = useValueHasChangedCallback(() => {
    trackEditEvent(action, category);
  });

  return { onBlur, onFocus };
};
